import { GridApiPro, GridRowId } from "@mui/x-data-grid-pro";
import { CrudActionButtonDefinition } from "src/components/common/crud/crudActionButtonDefinition.ts";
import { getDataGridRowsByIds } from "src/components/common/dataGrid/utils.ts";
import i18n from "i18next";

interface UseCrudGridRemoveActionParams<RowData extends object, SelectedRowData extends object = RowData> {
	remove: CrudActionButtonDefinition<
		{
			row: RowData;
			items: RowData[];
		},
		unknown
	>;
	selectedRowIds: GridRowId[];
	getGridApi: () => GridApiPro;
	getRows?: () => readonly SelectedRowData[] | undefined;
	getRowId?: (row: SelectedRowData) => GridRowId;
	shouldCallRemoveAction?: (row: SelectedRowData) => row is RowData & SelectedRowData;
	onRowsRemoved: (params: {
		selectedRows: SelectedRowData[];
		removedByActionRows: RowData[];
		selectedRowIds: GridRowId[];
	}) => Promise<void> | void;
	clearSelection: () => void;
}

export function useCrudGridRemoveAction<RowData extends object, SelectedRowData extends object = RowData>({
	remove,
	selectedRowIds,
	getGridApi,
	getRows,
	getRowId,
	shouldCallRemoveAction,
	onRowsRemoved,
	clearSelection,
}: UseCrudGridRemoveActionParams<RowData, SelectedRowData>): CrudActionButtonDefinition {
	if (remove?.type !== "enabled") return remove ?? { type: "hidden" };

	if (selectedRowIds.length === 0) {
		return {
			type: "disabled",
			tooltip: i18n.t("select_at_least_one_row"),
		};
	}

	return {
		...remove,
		action: removeSelectedRows,
	};

	async function removeSelectedRows(): Promise<"interrupt" | void> {
		if (remove?.type !== "enabled") return;

		const selectedRows = getSelectedRows();
		if (selectedRows.length === 0) return;

		const removedByActionRows = selectedRows.filter(shouldCallRemoveActionResolved);
		if (removedByActionRows.length > 0) {
			const result = await remove.action({
				row: removedByActionRows[0]!,
				items: removedByActionRows,
			});
			if (result === "interrupt") return "interrupt";
		}

		await onRowsRemoved({
			selectedRows,
			removedByActionRows,
			selectedRowIds,
		});
		clearSelection();
	}

	function getSelectedRows(): SelectedRowData[] {
		const rows = getRows?.();
		if (rows != null && getRowId != null) {
			return rows.filter((row) => selectedRowIds.includes(getRowId(row)));
		}
		return getDataGridRowsByIds<SelectedRowData>(getGridApi(), selectedRowIds);
	}

	function shouldCallRemoveActionResolved(row: SelectedRowData): row is RowData & SelectedRowData {
		return shouldCallRemoveAction == null ? true : shouldCallRemoveAction(row);
	}
}
